import React from "react";

// Props passed to a Mention's `renderSuggestions` callback while a trigger is
// active. The host (MentionInput) owns selection/positioning; the renderer
// only draws the dropdown and forwards user intent back through these.
export interface SuggestionRenderProps {
  // Suggestions already filtered by `searchText`.
  suggestions: unknown[];
  // Text typed after the trigger (e.g. "ka" in "/ka").
  searchText: string;
  // Viewport-relative anchor (fixed positioning) of the trigger character.
  position: { top: number; left: number };
  // Commit a value at the cursor, replacing the in-progress query.
  onSelect: (value: string) => void;
  // Dismiss the dropdown without committing.
  onClose: () => void;
}

export interface MentionProps {
  // Trigger character that activates this mention type (e.g. "/").
  trigger: string;
  // Identifier stamped on committed blocks via `data-block-type`.
  type: string;
  // Matches committed tokens in the plain-text value. Auto-derived from the
  // trigger when omitted (trigger followed by word characters and hyphens).
  pattern?: RegExp;
  // Tailwind classes applied to committed inline token spans.
  className?: string;
  // Suggestion source: a static array (filtered client-side) or a query fn.
  data?: unknown[] | ((query: string, callback: (results: unknown[]) => void) => void | unknown[]);
  // Extracts the display string from a data item. Defaults to name/id.
  displayTransform?: (item: unknown) => string;
  // Append a trailing space after inserting. Defaults to true.
  appendSpaceOnAdd?: boolean;
  // Renders the suggestion dropdown. Required — there is no default UI.
  renderSuggestions: (props: SuggestionRenderProps) => React.ReactNode;
}

// Declarative configuration only — never renders. MentionInput reads these
// children via React.Children to build its trigger table.
export const Mention: React.FC<MentionProps> = () => null;

Mention.displayName = "Mention";
